// src/pages/locations/hooks/useDeleteConfirm.ts
import React from "react";

/**
 * Delete-confirmation state shared by the Locations tabs.
 *
 * Deleting stays a dialog on purpose: a confirmation is not a destination, so
 * it does not get its own route the way create/edit do.
 */
export type DeleteConfirmState<TRow> = {
  open: boolean;
  row: TRow | null;
};

export function useDeleteConfirm<TRow>(params: {
  isDeleting: boolean;
  /** Runs the delete; call `done` when the request settles. */
  remove: (row: TRow, done: () => void) => void;
}) {
  const { isDeleting, remove } = params;

  const [confirmDelete, setConfirmDelete] = React.useState<
    DeleteConfirmState<TRow>
  >({ open: false, row: null });

  const askDelete = React.useCallback((row: TRow) => {
    setConfirmDelete({ open: true, row });
  }, []);

  const cancelDelete = React.useCallback(() => {
    if (isDeleting) return;
    setConfirmDelete({ open: false, row: null });
  }, [isDeleting]);

  const confirmDeleteRow = React.useCallback(() => {
    const row = confirmDelete.row;
    if (!row) return;

    // Fire the delete from the handler, never from inside a state updater —
    // React invokes updaters twice under StrictMode, which would double-send it.
    remove(row, () => setConfirmDelete({ open: false, row: null }));
  }, [confirmDelete.row, remove]);

  return { confirmDelete, askDelete, cancelDelete, confirmDeleteRow };
}
